<template>
  <form @submit.prevent="saveImage" class="flex flex-wrap">
    <div
        v-if="isBusy"
        class="absolute inset-0 flex justify-center items-center bg-white bg-opacity-75 z-50"
    >
      <ProgressSpinner style="width:50px;height:50px" />
    </div>

    <p>Schedule or record an event for this enquiry</p>

    <div class="w-full py-2">
      <label class="uppercase font-semibold text-sm">Image Name:</label>
      <input
          class="w-full bge-input bge-input-spacing rounded"
          v-model="name"
          placeholder="Image Name..."
          type="text"
      />
    </div>

    <div class="w-full py-2">
      <label class="uppercase font-semibold text-sm">Image Description:</label>
      <textarea
          v-model="description"
          class="w-full bge-input bge-input-spacing rounded"
          placeholder="Image Description..."
          rows="10"
      ></textarea>
    </div>

    <div v-if="status == 'Create'" class="w-full py-2">
      <label class="uppercase font-semibold text-sm">Image:</label>

      <div
          @drop.prevent="onDropFiles"
          @dragover.prevent
          @change="onFilesChange"
          class="relative w-full rounded border-2 border-dashed border-gray-300 bg-gray-50 hover:bg-gray-100 transition duration-150"
          style="min-height: 130px;"
      >
        <input
            multiple
            class="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            type="file"
            accept="image/*"
        />

        <div class="h-full w-full flex flex-col items-center justify-center text-center px-6 py-6">
          <div class="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center mb-3">
            <svg
                class="stroke-current h-6 w-6 text-blue-600"
                fill="none"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                viewBox="0 0 24 24"
            >
              <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
              <path d="M14 2v6h6M12 18v-6M9 15h6" />
            </svg>
          </div>

          <p class="text-sm font-semibold text-secondary">Browse Images or Drop Here</p>
          <p class="text-xs text-gray-500 mt-1">PNG, JPG, JPEG supported</p>
        </div>
      </div>
    </div>


    <div v-if="status == 'Create'" class="w-full py-2">
      <template v-for="(image, i) in tempImages">
        <div :key="i">{{ image.file && image.file.name ? image.file.name : "" }}</div>
      </template>
    </div>

    <div class="w-full">
      <button
          type="submit"
          class="float-right mt-5 rounded border bg-green-200 border-green-400 text-green-800 py-1 px-3 hover:bg-green-300 hover:border-green-500 hover:shadow"
      >
        {{ status == "Create" ? "Upload Images" : "Save Image" }}
      </button>
    </div>

    <div v-if="uploadProgress > 0" class="w-full py-4">
      <label class="uppercase font-semibold text-sm">Upload Progress</label>
      <div class="w-full bg-gray-200 rounded-full h-4">
        <ProgressBar :value="uploadProgress" />
      </div>
    </div>
  </form>
</template>

<script>
import notificationMixin from "@/mixins/notificationMixin";

export default {
  mixins: [notificationMixin],
  name: "AddEditImage",
  components: {},
  props: {
    images: {
      required: true,
      type: Array,
    },
    uploadRefId: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      status: "",
      name: "",
      description: "",
      document_id: "",
      tempImages: [],
      uploadProgress: 0,
      isBusy: false,
    };
  },
  mounted() {
    this.tempImages = this.images;

    if (this.tempImages && this.tempImages.length === 1) {
      this.status = "Edit";
      this.name = this.tempImages[0] && this.tempImages[0].name ? this.tempImages[0].name : "";
      this.description =
          this.tempImages[0] && this.tempImages[0].description ? this.tempImages[0].description : "";
      this.document_id =
          this.tempImages[0] && this.tempImages[0].document_id ? this.tempImages[0].document_id : "";
    } else {
      this.status = "Create";
    }
  },
  methods: {
    onDropFiles: function (e) {
      if (this.status === "Create") {
        this.handleAttachments(e.dataTransfer.files);
      }
    },

    onFilesChange: function (e) {
      if (this.status === "Create") {
        this.handleAttachments(e.target.files);
      }
    },

    handleAttachments: function (fileList) {
      if (!fileList || typeof fileList.length !== "number") return;

      for (let i = 0; i < fileList.length; i++) {
        const file = fileList[i];
        if (!file) continue;

        const ext = this.getFileExtension(file);
        const isImg = this.isImageExt(ext);
        if (!isImg) continue;

        const fileUrl = URL.createObjectURL(file);
        const uniqueId = this.generateUniqueId();

        const filename = file && file.name ? String(file.name) : "image." + ext;

        this.tempImages.push({
          access_url: fileUrl,
          access_url_thumbnail: fileUrl,
          filename: filename,
          original_filename: filename,
          name: this.name,
          description: this.description,
          isNew: true,
          content_type: ext,
          unique_id: uniqueId,
          original_file: file,
          file: file,
          requires_thumbnail: true,
          document_role: "image",
          progress: 0,
        });
      }
    },

    saveImage: async function () {
      if (!this.name || !String(this.name).trim()) {
        this.notifyError("Image Name is required.");
        return;
      }

      if (!this.description || !String(this.description).trim()) {
        this.notifyError("Image Description is required.");
        return;
      }

      this.isBusy = true;
      try {
        // EDIT: update metadata only
        if (this.status === "Edit") {
          if (!this.uploadRefId) {
            this.notifyError("Please save the details before updating the image.");
            return;
          }
          if (!this.document_id) {
            this.notifyError("Document ID is missing.");
            return;
          }

          await this.ProductServiceV2.updateProductDocument(
              this.uploadRefId, // productId
              this.document_id, // documentId
              {
                name: this.name,
                description: this.description,
              },
          );

          this.notifySuccess("Image updated successfully.");
        } else {
          // CREATE: upload new images (create doc records + upload blobs + mark is_uploaded)
          const newFilesToUpload = (this.tempImages || []).filter(function (f) {
            return f && f.isNew === true;
          });

          if (!newFilesToUpload || newFilesToUpload.length === 0) {
            this.notifyWarning("No new images to upload.");
            return;
          }

          await this.uploadImagesWithThumbnails(newFilesToUpload);
          this.notifySuccess("Images uploaded successfully.");
        }
      } catch (error) {
        console.error("Error during saveImage:", error);
        this.notifyError("An error occurred during the operation.");
      } finally {
        this.isBusy = false;
        this.$emit("complete");
      }
    },

    async uploadImagesWithThumbnails(newFilesToUpload) {
      if (!this.uploadRefId) {
        this.notifyError("Please save the details before uploading images.");
        return;
      }

      // 1) Build new API payload
      const payload = [];
      for (let i = 0; i < newFilesToUpload.length; i++) {
        const f = newFilesToUpload[i];
        if (!f) continue;

        const fileObj = f.original_file;
        if (!fileObj) continue;

        const ext = this.getFileExtension(fileObj); // extension only
        const safeFilename = this.ensureFilenameHasExtension(f.filename, ext);

        payload.push({
          filename: safeFilename, // MUST contain extension
          content_type: ext, // MUST be extension-only for new model
          visibility: "Internal",
          category: "products",
          name: f.name,
          description: f.description,
          unique_id: f.unique_id,
          requires_thumbnail: true,
          document_role: "image",
        });
      }

      if (!payload.length) {
        this.notifyWarning("No valid images to upload.");
        return;
      }

      this.uploadProgress = 0;

      // 2) Create document records (returns SAS URLs)
      const uploadResults = await this.ProductServiceV2.uploadProductDocuments(
          payload,
          this.uploadRefId, // productId
          "image", // resourceRole for product images
      );

      const resultsArray = Array.isArray(uploadResults)
          ? uploadResults
          : uploadResults
              ? [uploadResults]
              : [];

      // 3) Upload blobs (main + thumbnail) then mark is_uploaded
      for (let r = 0; r < resultsArray.length; r++) {
        const uploadResult = resultsArray[r];
        if (!uploadResult) continue;

        const uid = uploadResult.unique_id;
        let fileToUpload = null;

        for (let j = 0; j < newFilesToUpload.length; j++) {
          const f = newFilesToUpload[j];
          if (f && f.unique_id === uid) {
            fileToUpload = f;
            break;
          }
        }

        if (!fileToUpload || !fileToUpload.original_file) continue;

        // Upload main image
        if (uploadResult.access_url) {
          await this.uploadFileToBlob(uploadResult.access_url, fileToUpload.original_file);
        }

        // Upload thumbnail (if provided by backend)
        if (uploadResult.access_url_thumbnail) {
          const originalExt = this.getFileExtension(fileToUpload.original_file);
          const thumbFile = await this.resizeImage(fileToUpload.original_file, originalExt);
          await this.uploadFileToBlob(uploadResult.access_url_thumbnail, thumbFile);
        }

        // Mark uploaded so it can be viewed/downloaded
        if (uploadResult.document_id) {
          await this.ProductServiceV2.updateProductDocument(
              this.uploadRefId,
              uploadResult.document_id,
              { is_uploaded: true },
          );
        }
      }
    },

    // --- helpers (no optional chaining) ---
    isImageExt(ext) {
      const e = ext ? String(ext).toLowerCase() : "";
      return ["jpg", "jpeg", "png", "gif", "bmp"].indexOf(e) >= 0;
    },

    getFileExtension(file) {
      // returns extension only, lower-case (jpg/jpeg/png/pdf...)
      if (file && file.name) {
        const name = String(file.name);
        const parts = name.split(".");
        if (parts.length >= 2) return String(parts.pop()).toLowerCase();
      }

      if (file && file.access_url) {
        const url = String(file.access_url);
        const parts = url.split(".");
        if (parts.length >= 2) return String(parts.pop()).toLowerCase();
      }

      return "";
    },

    ensureFilenameHasExtension(filename, ext) {
      const name = filename ? String(filename) : "";
      const hasDot = name.indexOf(".") >= 0;
      if (hasDot) return name;

      const e = ext ? String(ext).toLowerCase().replace(".", "") : "";
      if (!e) return name;

      return name + "." + e;
    },

    stripExt(name) {
      const n = name ? String(name) : "";
      const idx = n.lastIndexOf(".");
      return idx > 0 ? n.substring(0, idx) : n;
    },

    async resizeImage(file, originalExt) {
      // Keep png thumbnails as png; everything else -> jpeg
      const ext = originalExt ? String(originalExt).toLowerCase() : "";
      const outMime = ext === "png" ? "image/png" : "image/jpeg";
      const outExt = ext === "png" ? "png" : "jpeg";

      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          const img = new Image();
          img.onload = () => {
            const canvas = document.createElement("canvas");
            const ctx = canvas.getContext("2d");

            const maxWidth = 300;
            const maxHeight = 225;

            const ratio = Math.min(maxWidth / img.width, maxHeight / img.height);
            canvas.width = Math.round(img.width * ratio);
            canvas.height = Math.round(img.height * ratio);

            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

            canvas.toBlob(
                (blob) => {
                  if (!blob) {
                    reject(new Error("Canvas to Blob conversion failed."));
                    return;
                  }

                  const base = file && file.name ? this.stripExt(file.name) : "thumbnail";
                  const thumbName = "thumb_" + base + "." + outExt;

                  const resizedFile = new File([blob], thumbName, {
                    type: outMime,
                    lastModified: Date.now(),
                  });

                  resolve(resizedFile);
                },
                outMime,
                outMime === "image/jpeg" ? 0.8 : 0.92,
            );
          };
          img.onerror = () => reject(new Error("Image load error."));

          if (e && e.target && e.target.result) {
            img.src = e.target.result;
          } else {
            reject(new Error("Invalid FileReader result."));
          }
        };
        reader.onerror = () => reject(new Error("FileReader error."));
        reader.readAsDataURL(file);
      });
    },

    async uploadFileToBlob(sasUrl, file) {
      const url = new URL(sasUrl);
      const accountUrl = url.origin;
      const sasToken = url.search;

      const pathParts = url.pathname.split("/").filter((p) => {
        return p && p.trim() !== "";
      });
      const container = pathParts.length > 0 ? pathParts[0] : "";
      const filename = pathParts.slice(1).join("/");

      const blobService = AzureStorage.Blob.createBlobServiceWithSas(accountUrl, sasToken);

      const customBlockSize =
          file && file.size > 1024 * 1024 * 32 ? 1024 * 1024 * 4 : 1024 * 128;
      blobService.singleBlobPutThresholdInBytes = customBlockSize;

      return new Promise((resolve, reject) => {
        const speedSummary = blobService.createBlockBlobFromBrowserFile(
            container,
            filename,
            file,
            { blockSize: customBlockSize },
            (error) => {
              if (error) {
                reject(error);
                return;
              }
              this.uploadProgress = 100;
              resolve();
            },
        );

        speedSummary.on("progress", () => {
          let p = speedSummary.getCompletePercent();
          if (typeof p === "number") {
            // support both 0..1 and 0..100
            if (p <= 1) p = p * 100;
            this.uploadProgress = Math.round(p * 100) / 100;
          }
        });
      });
    },

    generateUniqueId() {
      return "id_" + Date.now() + "_" + Math.random().toString(36).slice(2, 11);
    },
  },
};
</script>
